import React from 'react';

import { schema } from '@rover/components/src/microdata';
import { Box, Text } from '@rover/kibble/core';

import A11yHiddenBox from '../../styles/A11yHiddenBox';

import {
  generateStars,
  getAccessibilityLabel,
  getStarRating,
  MAX_RATING,
  MIN_RATING,
  StarRatingCommonProps,
} from './StarRating.common';

export type Props = StarRatingCommonProps & {
  /** Text to appear alongside the numeric rating. Only shows in `"compact"` variant */
  ratingText?: string;
  onClick?: () => void;
  /** When set, the schema type becomes AggregateRating and a reviewCount meta tag is rendered */
  reviewCount?: number;
};

export const StarRating: React.FC<Props> = ({
  rating,
  variant = 'compact',
  maxRating = MAX_RATING,
  minRating = MIN_RATING,
  textSize = '200',
  fontWeight = 'regular',
  disabled = false,
  ratingText,
  onClick,
  emptyValue,
  reviewCount,
  ...other
}) => {
  const starRating = getStarRating(rating, maxRating);
  const stars: JSX.Element[] = generateStars(
    variant,
    emptyValue ? null : starRating,
    maxRating,
    disabled
  );
  const accessibilityLabel = getAccessibilityLabel(starRating, maxRating);
  const translatedRatingText = ratingText || undefined;
  const isClickable = !!onClick;

  return (
    <Box
      {...other}
      display="flex"
      flexDirection="row"
      alignItems="center"
      webProps={{
        itemScope: true,
        itemType: reviewCount != null ? schema`AggregateRating` : schema`Rating`,
      }}
    >
      <A11yHiddenBox>{accessibilityLabel}</A11yHiddenBox>
      <Box aria-hidden display="flex" flexDirection="row">
        {stars}
        <meta itemProp="ratingValue" content={starRating.toString()} />
        <meta itemProp="bestRating" content={maxRating.toString()} />
        <meta itemProp="worstRating" content={minRating.toString()} />
        {reviewCount != null && <meta itemProp="reviewCount" content={reviewCount.toString()} />}
      </Box>
      <Box display="flex">
        {variant === 'compact' && (
          <Text
            aria-hidden
            textDecoration={isClickable ? 'underline' : 'none'}
            onPress={onClick}
            paddingLeft="1x"
            size={textSize}
            fontWeight={fontWeight}
            textColor={disabled ? 'disabled' : 'primary'}
            sx={{ cursor: isClickable ? 'pointer' : 'default' }}
          >
            {emptyValue ?? starRating.toFixed(1)}
            {translatedRatingText && ` ${translatedRatingText}`}
          </Text>
        )}
      </Box>
    </Box>
  );
};

export default StarRating;
